import {Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {ApplicationUsageRating, UsageService} from 'src/app/services/usage.service';
import {RightSliderService} from 'src/app/services/front/right-slider.service';
import {ApplicationDetailComponent, ApplicationDetailInput} from '../../applications/application-detail/application-detail.component';
import {CurrentTenantService} from 'src/app/services/front/current-tenant.service';
import {finalize, Observable, of, Subject, Subscription, switchMap, tap} from 'rxjs';
import {Organization} from 'src/app/services/organization.service';
import {QueryRangeType} from 'src/app/services/back/tenant-finance.service';
import {MostUsedAppsDrawerComponent, MostUsedAppsDrawerData} from 'src/app/modules/home/usage-dashboard/most-used-apps/drawer/most-used-apps-drawer.component';
import {ExportService, ExportType} from 'src/app/services/front/export.service';
import {ColumnDefinition} from "../../../design-system/table/table.component";

@Component({
  selector: 'app-most-used-apps',
	standalone: false,
  templateUrl: './most-used-apps.component.html',
  styleUrls: ['./most-used-apps.component.scss']
})
export class MostUsedAppsComponent implements OnInit, OnDestroy {

	@Input() defaultFilter?: Organization|null|undefined;
	@Input() filter?: Subject<Organization|null|undefined>;
	@Input() search?: Observable<string|null|undefined>;
	@Input() export?: Subject<ExportType>;
	@Input() limit?: number;
	@Input() type: 'widget'|'drawer' = 'widget';

	tenantId: string;
	lastFilter?: Organization|null;

	_initializing: boolean;
	_loading: boolean;

	applications: ApplicationUsageRating[] = [];
	filteredApplications: ApplicationUsageRating[] = [];

	columnDefinitions: ColumnDefinition<ApplicationUsageRating>[];

	initializeSub: Subscription;
	subscription: Subscription = new Subscription();

	@ViewChild('applicationTemplate', { static: true }) applicationTemplate: TemplateRef<any>;
	@ViewChild('popularityTemplate', { static: true }) popularityTemplate: TemplateRef<any>;
	@ViewChild('usageTemplate', { static: true }) usageTemplate: TemplateRef<any>;


	constructor(private currentTenantService: CurrentTenantService,
							private rightSliderService: RightSliderService,
							private usageService: UsageService,
							private exportService: ExportService) {
	}

	ngOnInit() {
		this.subscription.add(this.currentTenantService.getInitializingChanges()
			.subscribe(initializing => this._initializing = initializing));
		this.subscription.add(this.currentTenantService.getCurrentTenantIdChanges()
			.pipe(tap(tenantId => this.tenantId = tenantId))
			.subscribe(() => this.initialize(this.defaultFilter)));
		if (this.filter) {
			this.subscription.add(this.filter
				.pipe(tap(filter => this.lastFilter = filter))
				.subscribe(filter => this.initialize(filter)));
		}
		if (this.search) {
			this.subscription.add(this.search
				.subscribe(search => this.filterApplications(search!)));
		}
		if (this.export) {
			this.subscription.add(this.export
				.subscribe(type => this.exportData(type)));
		}
	}

	initialize(org?: Organization|null): void {
		this.initializeSub?.unsubscribe();
		this.initializeSub = this.switchLoading()
			.pipe(
				tap(() => this.setColumnDefinitions()),
				switchMap(() => this.usageService.getAllMostUsedApplicationByTenantId(this.tenantId, org?.organizationId, this.limit, QueryRangeType.PAST_1_MONTH)),
				tap(data => this.applications = data),
				tap(data => this.filteredApplications = data),
				finalize(() => this.switchLoading()))
			.subscribe();
	}

	setColumnDefinitions(): void {
		this.columnDefinitions = [
			{
				name: 'application',
				label: 'global.table.application.name',
				template: this.applicationTemplate,
				sortFunction: (a, b) => a.application.name.localeCompare(b.application.name),
				width: 45,
			},
			{
				name: 'rating',
				label: 'global.table.application.rating',
				template: this.popularityTemplate,
				sortFunction: (a, b) => (a.rating.value ?? 0) - (b.rating.value ?? 0),
			},
			{
				name: 'usage',
				label: 'global.table.application.usage',
				template: this.usageTemplate,
				sortFunction: (a, b) => (a.usage.value ?? 0) - (b.usage.value ?? 0),
			}
		];
	}

	filterApplications(search?: string|null): void {
		this.filteredApplications = this.applications
			.filter(app => !search || app.application.name.toLowerCase().includes(search.toLowerCase()));
	}

	goToApp(applicationInstanceId: string): void {
		const data: ApplicationDetailInput = {
			applicationId: applicationInstanceId
		};
		this.rightSliderService.openComponent(ApplicationDetailComponent, data)
			.subscribe(() => this.filter?.next(this.lastFilter));
	}

	openDrawer(): void {
		if (this.type === 'widget' && this.applications.length > 0) {
			const data: MostUsedAppsDrawerData = {
				filter: this.lastFilter
			};
			this.rightSliderService.openComponent(MostUsedAppsDrawerComponent, data);
		}
	}

	exportData(type: ExportType): void {
		const rows: ApplicationUsageCsv[] = this.filteredApplications.map(a => ({
			name: a.application.name,
			popularity: a.rating.value?.toFixed(2) ?? '-',
			usage: a.usage.value?.toString() ?? '-'
		}));
		this.exportService.export(type, rows, 'application-usage');
	}

	onSorted(event: ApplicationUsageRating[]): void {
		this.filteredApplications = event;
	}

	onRowClick(event: ApplicationUsageRating): void {
		this.goToApp(event.application.id);
	}

	private switchLoading(): Observable<{}> {
		this._loading = !this._loading;
		return of({});
	}

	ngOnDestroy() {
		this.subscription.unsubscribe();
		this.initializeSub?.unsubscribe();
	}
}

interface ApplicationUsageCsv {
	name: string;
	popularity: string;
	usage: string;
}
